// Teifi Wishlists storefront API client, used to back the Job Lists page
// (each Job List is a Wishlist under the hood — same API assets/saved-carts.js
// uses for the "Saved Carts" cart tab). Kept as its own file instead of sharing
// saved-carts.js's copy so this ticket doesn't touch that already-shipped code.
// Docs: FoxTheme.wishlist.proxyPath + /api/docs/storefront/wishlists

const idFromGid = (gid) => String(gid).split('/').pop();

// Shopify's `t` filter HTML-escapes translations whose key doesn't end in `_html`
// (so `'` becomes `&#39;`, etc). That's correct when the string lands in markup,
// but these strings are used as plain JS text/attributes, which never decodes
// entities — so decode once, here, before anything reads them.
if (FoxTheme.jobListsStrings) {
  const el = document.createElement('textarea');
  for (const key of Object.keys(FoxTheme.jobListsStrings)) {
    el.innerHTML = FoxTheme.jobListsStrings[key];
    FoxTheme.jobListsStrings[key] = el.value;
  }
}

const WishlistAPI = {
  get base() {
    return `${FoxTheme.wishlist?.proxyPath ?? '/apps/wishlists'}/api/storefront/wishlists`;
  },

  async request(path, options = {}) {
    const res = await fetch(`${this.base}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...options.headers,
      },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? `HTTP ${res.status}`);
    }
    if (res.status === 204) return null;
    return res.json();
  },

  // GET /api/storefront/wishlists -> { items, page, limit, totalItems, totalPages }
  getLists(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(qs ? `?${qs}` : '');
  },

  // GET /api/storefront/wishlists/:id
  getList(id) {
    return this.request(`/${id}`);
  },

  // POST /api/storefront/wishlists  { name, description, items }
  createList(name, description = '', items = []) {
    return this.request('', {
      method: 'POST',
      body: JSON.stringify({ name, description, items: items.map(this.toWritableItem) }),
    });
  },

  // POST /api/storefront/wishlists/:id
  updateList(id, data) {
    return this.request(`/${id}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // DELETE /api/storefront/wishlists/:id
  deleteList(id) {
    return this.request(`/${id}`, { method: 'DELETE' });
  },

  // POST /api/storefront/wishlists/:id/shareable  { shareable } -> { ..., sharedCode, sharedUrl }
  updateShareable(id, shareable) {
    return this.request(`/${id}/shareable`, {
      method: 'POST',
      body: JSON.stringify({ shareable }),
    });
  },

  // GET + merge + POST: the API has no add-item endpoint. Same variant -> quantities add up.
  async addItems(id, newItems) {
    const wishlist = await this.getList(id);
    const items = (wishlist.items ?? []).map(this.toWritableItem);
    for (const newItem of newItems.map(this.toWritableItem)) {
      const existing = items.find((item) => item.variantGid === newItem.variantGid);
      if (existing) existing.quantity += newItem.quantity;
      else items.push(newItem);
    }
    return this.updateList(id, {
      name: wishlist.name,
      handle: wishlist.handle ?? '',
      description: wishlist.description ?? '',
      items,
    });
  },

  // A <quick-order-item> row (snippets/quick-order-item.liquid) -> writable list item.
  itemFromRow(row) {
    return {
      productGid: `gid://shopify/Product/${row.dataset.productId}`,
      variantGid: `gid://shopify/ProductVariant/${row.dataset.variantId}`,
      handle: row.dataset.handle ?? '',
      quantity: Math.max(1, Number(row.querySelector('[data-item-quantity]')?.value) || 1),
      note: '',
    };
  },

  // Only the writable fields are accepted by the API — price, image, etc. are server-populated.
  toWritableItem(item) {
    return {
      productGid: item.productGid,
      variantGid: item.variantGid,
      handle: item.handle ?? '',
      quantity: item.quantity ?? 1,
      note: item.note ?? '',
    };
  },
};

// The storefront wishlists endpoint only stores productGid/variantGid/handle/quantity/note —
// no price or image. Look those up from Shopify's public product JSON (keyed by handle so
// items sharing a product only fetch it once) and graft them onto the item for display.
const wishlistProductByHandle = new Map();

function fetchWishlistProductByHandle(handle) {
  if (!handle) return Promise.resolve(null);
  if (!wishlistProductByHandle.has(handle)) {
    wishlistProductByHandle.set(
      handle,
      fetch(`/products/${handle}.js`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null)
    );
  }
  return wishlistProductByHandle.get(handle);
}

async function enrichWishlistItems(items) {
  const products = await Promise.all(items.map((item) => fetchWishlistProductByHandle(item.handle)));
  return items.map((item, index) => {
    const product = products[index];
    const variantId = Number(idFromGid(item.variantGid));
    const variant = product?.variants?.find((v) => v.id === variantId) ?? product?.variants?.[0];
    return {
      ...item,
      imageUrl: item.imageUrl || variant?.featured_image?.src || product?.featured_image || '',
    };
  });
}

// ─────────────────────────────────────────────────────────────────
// Cart helpers — used by the "Add List to Cart" row action
// ─────────────────────────────────────────────────────────────────
function collectCartSectionsToBundle() {
  const sections = [];
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
  );
  return sections;
}

async function getShopifyCart() {
  const res = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
  return res.json();
}

async function addItemsToShopifyCart(items) {
  const payload = items
    .filter((i) => i.variantGid)
    .map((i) => ({ id: Number(idFromGid(i.variantGid)), quantity: i.quantity ?? 1 }));
  if (!payload.length) return null;

  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ items: payload, sections: collectCartSectionsToBundle() }),
  });
  if (!res.ok) throw new Error(`Failed to add items to cart (HTTP ${res.status})`);
  return res.json();
}

// /cart/add.js doesn't carry cart-level fields like item_count that onCartUpdate needs
// (only /cart.js does) — fetch the real cart and graft the rendered `sections` onto it.
async function publishCartRefresh(sections) {
  const cartJson = await getShopifyCart();
  cartJson.sections = sections;
  FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
  return cartJson;
}
